import { Alert, Badge, Button, Card, cn } from "@artisan/ui";
import { Check, CircleAlert, CloudUpload, Loader2, ServerCog, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { Navigate, useParams } from "react-router";

import { Screen } from "../components/screen";
import { authFetch } from "../lib/auth";
import { apiUrl } from "../lib/env";
import { nowIso, relativeMn } from "../lib/time";
import { kick, latestLive, queueRevision, useOutboxStatus } from "../outbox/outbox";
import { projectTitle } from "../project/actions";
import { isSent } from "../project/progress";
import { useProject } from "../project/store";
import { buildSurvey, encodeSurvey } from "../survey/build";
import { type FieldIssue, reviewProject } from "../survey/checks";
import type { SurveyIssue } from "../survey/types";

function deviceInfo() {
  const ua = navigator.userAgent;
  const ipad = /iPad|Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return { model: ipad ? "iPad" : /Android/.test(ua) ? "Android tablet" : "Browser", os: (ua.match(/(iPhone OS|CPU OS|Android) [\d_.]+/)?.[0] ?? "").replace(/_/g, ".") };
}

function Issues({ issues }: { issues: { severity: string; message: string }[] }) {
  return (
    <ul className="space-y-2">
      {issues.map((i, k) => (
        <li key={k} className="flex gap-2 text-sm">
          {i.severity === "error" ? (
            <CircleAlert className="mt-0.5 size-4 shrink-0 text-destructive" aria-label="Алдаа" />
          ) : (
            <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warning" aria-label="Анхааруулга" />
          )}
          <span>{i.message}</span>
        </li>
      ))}
    </ul>
  );
}

export function SendScreen() {
  const { id } = useParams();
  const project = useProject(id);
  const outbox = useOutboxStatus();
  const [server, setServer] = useState<{ errors: SurveyIssue[]; warnings: SurveyIssue[] } | null>(null);
  const [checking, setChecking] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  if (!project) return <Navigate to="/" replace />;
  const current = project;
  const local: FieldIssue[] = reviewProject(project);
  const errors = local.filter((i) => i.severity === "error");
  const sent = isSent(project);
  const parent = latestLive(project);

  async function check() {
    setChecking(true);
    setNote(null);
    try {
      const doc = buildSurvey(current, { revision_id: crypto.randomUUID(), parent_revision_id: parent?.revision_id ?? null, captured_at: nowIso() });
      const res = await authFetch(new Request(apiUrl("/v1/factory/surveys/validate"), { method: "POST", headers: { "content-type": "application/json" }, body: encodeSurvey(doc) }));
      if (!res.ok) throw new Error(String(res.status));
      setServer((await res.json()) as { errors: SurveyIssue[]; warnings: SurveyIssue[] });
    } catch {
      setNote("Сервертэй холбогдож чадсангүй. Офлайн үед илгээх дараалалд хадгалагдана.");
    } finally {
      setChecking(false);
    }
  }

  async function send() {
    setNote(null);
    const r = await queueRevision(current.id, deviceInfo());
    if (r.kind === "unchanged") setNote("Сүүлд илгээснээс хойш өөрчлөлт алга.");
    if (r.kind === "not_ready") setNote(r.messages[0] ?? "Хэмжилт дутуу байна.");
  }

  return (
    <Screen back={`/p/${project.id}`} title="Үйлдвэрт илгээх" subtitle={projectTitle(project)}>
      <div className="grid grid-cols-[1.2fr_1fr] gap-6">
        <div className="space-y-5">
          <Card className="space-y-4 p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold">Шалгалт</h2>
              {errors.length ? <Badge variant="destructive">{errors.length} алдаа</Badge> : <Badge variant="success" icon={Check}>Илгээхэд бэлэн</Badge>}
            </div>
            {local.length ? <Issues issues={local} /> : <p className="text-sm text-muted-foreground">Апп дээрх шалгалтад алдаа алга.</p>}
            <div className="flex items-center gap-3 border-t pt-4">
              <Button variant="outline" icon={checking ? Loader2 : ServerCog} disabled={checking || errors.length > 0} onClick={() => void check()}>
                Сервераар шалгуулах
              </Button>
              {server ? (
                server.errors.length ? (
                  <Badge variant="destructive">Сервер: {server.errors.length} алдаа</Badge>
                ) : (
                  <Badge variant="success" icon={Check}>
                    Сервер зөвшөөрнө{server.warnings.length ? ` · ${server.warnings.length} анхааруулга` : ""}
                  </Badge>
                )
              ) : null}
            </div>
            {server && (server.errors.length || server.warnings.length) ? (
              <Issues issues={[...server.errors, ...server.warnings].map((i) => ({ severity: i.severity, message: i.message_mn }))} />
            ) : null}
          </Card>
          <Card className="space-y-4 p-6">
            <p className="text-sm text-muted-foreground">
              Илгээсэн хувилбар өөрчлөгдөхгүй. Дахин хэмжвэл шинэ хувилбар болж, өмнөх нь түүхэнд үлдэнэ. Интернэтгүй үед дараалалд хадгалагдаж, сүлжээ орж
              ирэхэд автоматаар явна.
            </p>
            {note ? <Alert variant="warning">{note}</Alert> : null}
            <Button size="lg" className="w-full" icon={CloudUpload} disabled={errors.length > 0 || sent} onClick={() => void send()}>
              {sent ? "Илгээсэн, өөрчлөлт алга" : parent ? "Шинэ хувилбар илгээх" : "Үйлдвэрт илгээх"}
            </Button>
          </Card>
        </div>
        <Card className="space-y-4 p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">Хувилбарууд</h2>
            {outbox.pending ? (
              <Button variant="ghost" size="sm" onClick={() => void kick()}>
                Одоо илгээх
              </Button>
            ) : null}
          </div>
          {project.revisions.length === 0 ? <p className="text-sm text-muted-foreground">Одоогоор илгээгээгүй.</p> : null}
          <ol className="space-y-3">
            {[...project.revisions].reverse().map((r) => (
              <li key={r.revision_id} className={cn("rounded-lg border p-3", r.state === "rejected" && "border-destructive/40 bg-destructive-soft/40")}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">{r.revision_no ? `v${r.revision_no}` : "Шинэ хувилбар"}</span>
                  <Badge variant={r.state === "sent" ? "success" : r.state === "rejected" ? "destructive" : "warning"}>
                    {r.state === "sent" ? "Хүлээн авсан" : r.state === "rejected" ? "Татгалзсан" : "Дараалалд"}
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {relativeMn(r.created_at)} · {(r.size_bytes / 1024).toFixed(1)} KB
                  {r.photos_pending ? ` · ${r.photos_pending} зураг хүлээгдэж байна` : ""}
                </p>
                {r.errors?.length ? <Issues issues={r.errors.map((e) => ({ severity: "error", message: e.message_mn }))} /> : null}
                {r.warnings?.length ? <Issues issues={r.warnings.map((w) => ({ severity: "warning", message: w.message_mn }))} /> : null}
              </li>
            ))}
          </ol>
        </Card>
      </div>
    </Screen>
  );
}
